import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  FaBars,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaTimes,
} from "react-icons/fa";

import { getSettings } from "../../services/settingsServices";
import { useUser } from "../../pages/Home/UserContext";

import { useDashboardLayout } from "../contexts/DashboardLayoutContext";

import {
  getUserRoleId,
  getVisibleDashboardSections,
  isDashboardMenuActive,
  unwrapSettings,
} from "../config/dashboardMenuConfig";
import { useMessage } from "../../hooks/MessageContext";

const TOPBAR_HEIGHT = "82px";
const STORAGE_KEY = "mac_dashboard_open_sections";

function getDefaultOpenSections(roleId) {
  if (roleId === 1) return ["utilisateur", "enseignant", "administrateur"];
  if (roleId === 3) return ["utilisateur", "enseignant"];
  return ["utilisateur"];
}

const formatUnreadBadge = (count) => {
  const value = Number(count || 0);
  if (value <= 0) return null;
  return value > 99 ? "99+" : String(value);
};

const isMessagingMenuItem = (item = {}) => {
  const raw = `${item.title || ""} ${item.path || ""}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  return (
    raw.includes("messagerie") ||
    raw.includes("message") ||
    raw.includes("messages")
  );
};

const getSectionBadgeCount = (section) => {
  return (section?.items || []).reduce((sum, item) => {
    const n = Number(item?.badgeValue || item?.badge || 0);
    return Number.isFinite(n) ? sum + n : sum;
  }, 0);
};

function SectionSeparator({ label, isCollapsed, mobile = false }) {
  if (isCollapsed && !mobile) {
    return (
      <div className="mx-3 my-4 flex items-center justify-center">
        <div className="h-px w-full bg-slate-200/90 dark:bg-slate-800/90" />
      </div>
    );
  }

  return (
    <div className="px-3 pb-2 pt-3">
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
        <p className="shrink-0 text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-500">
          {label}
        </p>
        <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  );
}

function MenuItem({ item, active, isCollapsed, mobile = false, onNavigate }) {
  const hasBadge = !!item.badge;

  return (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      title={item.title}
      className={`group mx-2 flex items-center rounded-2xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
        isCollapsed && !mobile ? "justify-center" : "gap-3"
      } ${
        active
          ? "bg-gradient-to-r from-[#013E8E] via-indigo-600 to-[#0f5cc0] text-white shadow-md hover:text-white"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
      }`}
    >
      <span
        className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-base transition-all ${
          active
            ? "bg-white/15 text-white group-hover:bg-white/20"
            : "bg-slate-100 text-slate-700 group-hover:bg-white dark:bg-slate-800 dark:text-slate-200 dark:group-hover:bg-slate-700"
        }`}
      >
        {item.icon}

        {hasBadge && isCollapsed && !mobile && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[10px] font-bold leading-none text-white shadow ring-2 ring-white dark:ring-slate-900">
            {item.badge}
          </span>
        )}
      </span>

      {(!isCollapsed || mobile) && (
        <span className="min-w-0 flex-1 truncate">{item.title}</span>
      )}

      {hasBadge && (!isCollapsed || mobile) && (
        <span
          className={`flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-[10px] font-bold leading-none shadow-sm ${
            active ? "bg-white text-[#013E8E]" : "bg-red-600 text-white"
          }`}
          title={`${item.badge} message(s) non lu(s)`}
        >
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}

function SectionBlock({
  section,
  pathname,
  isOpen,
  isCollapsed,
  mobile = false,
  onToggle,
  onNavigate,
}) {
  const hasActiveItem = section.items.some((item) =>
    isDashboardMenuActive(pathname, item),
  );

  const sectionBadgeCount = getSectionBadgeCount(section);
  const sectionBadge = formatUnreadBadge(sectionBadgeCount);

  if (isCollapsed && !mobile) {
    return (
      <div className="mb-2">
        <SectionSeparator
          label={section.label}
          isCollapsed={isCollapsed}
          mobile={mobile}
        />

        <div className="space-y-1">
          {section.items.map((item) => {
            const active = isDashboardMenuActive(pathname, item);

            return (
              <MenuItem
                key={item.path}
                item={item}
                active={active}
                isCollapsed={isCollapsed}
                mobile={mobile}
                onNavigate={onNavigate}
              />
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={onToggle}
        className={`mx-2 flex w-[calc(100%-1rem)] items-center gap-3 rounded-2xl border px-3 py-3 text-left transition-all ${
          hasActiveItem
            ? "border-transparent bg-gradient-to-r from-[#013E8E] via-indigo-600 to-[#0f5cc0] text-white shadow-md"
            : "border-slate-200 bg-slate-200 text-slate-800 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
        }`}
      >
        <span
          className={`h-2.5 w-2.5 shrink-0 rounded-full ${
            hasActiveItem ? "bg-primary" : "bg-primary"
          }`}
        />

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-bold font-montserrat">
            {section.label}
          </span>

          <span
            className={`mt-0.5 block text-xs ${
              hasActiveItem
                ? "text-white/80"
                : "text-slate-500 dark:text-slate-400"
            }`}
          >
            {section.items.length} page{section.items.length > 1 ? "s" : ""}
          </span>
        </span>

        {sectionBadge && (
          <span
            className={`flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-[10px] font-bold leading-none shadow-sm ${
              hasActiveItem
                ? "bg-white text-[#013E8E]"
                : "bg-red-600 text-white"
            }`}
            title={`${sectionBadgeCount} message(s) non lu(s)`}
          >
            {sectionBadge}
          </span>
        )}

        <span className="shrink-0 text-sm">
          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </button>

      {isOpen && (
        <div className="mt-2 space-y-1">
          {section.items.map((item) => {
            const active = isDashboardMenuActive(pathname, item);

            return (
              <MenuItem
                key={item.path}
                item={item}
                active={active}
                isCollapsed={isCollapsed}
                mobile={mobile}
                onNavigate={onNavigate}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DashboardAsideMenu() {
  const { user } = useUser();
  const { isCollapsed, setIsCollapsed } = useDashboardLayout();

  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const roleId = getUserRoleId(user);

  const [openSections, setOpenSections] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed)) return parsed;
    } catch {}

    return getDefaultOpenSections(roleId);
  });

  const { unreadMessageCount } = useMessage();

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const data = await getSettings();

        if (!mounted) return;

        setSettings(unwrapSettings(data));
      } catch (error) {
        console.error(
          "Erreur lors du chargement des settings du dashboard",
          error,
        );

        if (!mounted) return;

        setSettings({});
      } finally {
        if (mounted) setSettingsLoaded(true);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const baseSections = useMemo(() => {
    return getVisibleDashboardSections(user, settings);
  }, [user, settings]);

  const sections = useMemo(() => {
    const unreadBadge = formatUnreadBadge(unreadMessageCount);

    return baseSections.map((section) => ({
      ...section,
      items: (section.items || []).map((item) => {
        if (!isMessagingMenuItem(item)) return item;

        return {
          ...item,
          badge: unreadBadge,
          badgeValue: Number(unreadMessageCount || 0),
        };
      }),
    }));
  }, [baseSections, unreadMessageCount]);

  useEffect(() => {
    const allowedKeys = baseSections.map((section) => section.key);

    setOpenSections((prev) => {
      const cleaned = prev.filter((key) => allowedKeys.includes(key));

      if (cleaned.length > 0) return cleaned;

      return getDefaultOpenSections(roleId).filter((key) =>
        allowedKeys.includes(key),
      );
    });
  }, [roleId, baseSections]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(openSections));
    } catch {}
  }, [openSections]);

  const shellWidth = useMemo(() => {
    return isCollapsed ? "w-24" : "w-72";
  }, [isCollapsed]);

  const closeMobile = () => setMobileOpen(false);

  const toggleSection = (sectionKey) => {
    setOpenSections((prev) => {
      if (prev.includes(sectionKey)) {
        return prev.filter((key) => key !== sectionKey);
      }

      return [...prev, sectionKey];
    });
  };

  const renderSections = ({ mobile = false } = {}) => {
    if (!settingsLoaded && sections.length === 0) {
      return (
        <div className="mx-3 mt-4 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
          Chargement du menu...
        </div>
      );
    }

    if (sections.length === 0) {
      return (
        <div className="mx-3 mt-4 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
          Aucune page disponible pour ce profil.
        </div>
      );
    }

    return sections.map((section) => (
      <SectionBlock
        key={section.key}
        section={section}
        pathname={location.pathname}
        isOpen={openSections.includes(section.key)}
        isCollapsed={isCollapsed}
        mobile={mobile}
        onToggle={() => toggleSection(section.key)}
        onNavigate={mobile ? closeMobile : undefined}
      />
    ));
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="fixed left-4 z-[70] flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white/95 text-slate-800 shadow-lg backdrop-blur dark:border-slate-700 dark:bg-slate-900/95 dark:text-white lg:hidden"
        style={{ top: "98px" }}
        aria-label="Ouvrir le menu du dashboard"
      >
        <FaBars />
      </button>

      <aside
        className={`fixed left-0 z-40 hidden ${shellWidth} flex-col border-r border-slate-200/80 bg-white/95 backdrop-blur-xl transition-all duration-300 dark:border-slate-800/80 dark:bg-slate-900/95 lg:flex`}
        style={{
          top: TOPBAR_HEIGHT,
          height: `calc(100dvh - ${TOPBAR_HEIGHT})`,
        }}
      >
        <div className="flex items-center justify-end gap-3 px-4 pb-3 pt-4">
          <button
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="ml-auto flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label={isCollapsed ? "Déplier le menu" : "Replier le menu"}
          >
            {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        </div>

        {!isCollapsed && (
          <div className="mx-4 mb-4 rounded-2xl bg-gradient-to-r bg-primary p-4 text-white shadow-sm">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/70">
              Profil actif
            </p>
            <p className="mt-1 text-sm font-bold font-montserrat">
              {roleId === 1
                ? "Administrateur"
                : roleId === 3
                  ? "Enseignant"
                  : "Utilisateur"}
            </p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-1 pb-6">
          {renderSections()}
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-[80] lg:hidden">
          <div
            className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
            onClick={closeMobile}
          />

          <aside
            className="absolute left-0 w-[88vw] max-w-[360px] overflow-hidden border-r border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
            style={{
              top: TOPBAR_HEIGHT,
              height: `calc(100dvh - ${TOPBAR_HEIGHT})`,
            }}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4 dark:border-slate-800">
              <div>
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  MonAppliClub
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Navigation du dashboard
                </p>
              </div>

              <button
                type="button"
                onClick={closeMobile}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                aria-label="Fermer le menu"
              >
                <FaTimes />
              </button>
            </div>

            <div className="h-[calc(100%-82px)] overflow-y-auto px-2 pb-6 pt-3">
              {renderSections({ mobile: true })}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
